/* eslint react/jsx-max-depth: ["error", {max: 5}] -- applies to entire file */
/* eslint max-lines-per-function: ["error", {max: 190, skipBlankLines: true, skipComments: true}] -- very large/complex component */
/* eslint max-lines: ["error", {max: 261}] -- this applies for the entire file */

import { useCallback, useEffect, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { OnboardingScreenPath } from "@/constants/Navigation";
import {
  ConfirmationAlertTestID,
  OnboardingNotificationsTestID,
  TextTestID,
} from "@/constants/TestID";
import { useOnboarding } from "@/context-providers/OnboardingProvider";
import { router } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import usePushNotifications from "@/hooks/usePushNotifications";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetFullProfileQuery,
  useUpdateUserNotificationSettingsMutation,
} from "@/store/api/user";
import { NotificationSettingsSection } from "@/ui/components/notificationSettings";
import { getChangesForMixpanel } from "@/ui/components/notificationSettings/NotificationSettingsSection";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { Button, FAIconName, IconButton, Text } from "@/ui/core";
import { HStack, ScreenContainer, VStack } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { toMs } from "@/utils/date.util";
import { isIOS, isWeb } from "@/utils/platform.util";
import { createTimeoutPromise } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";
import { getNotificationSettings } from "@/utils/user.util";

const Screen = () => {
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const { registerForPushNotifications } = usePushNotifications();
  const { track, trackPageView } = useAnalytics();
  const { data: unmodifiedProfile } = useGetFullProfileQuery();
  const notifications = getNotificationSettings(unmodifiedProfile);
  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettings>(getNotificationSettings(unmodifiedProfile));
  const { shouldBypassConsentScreen } = useOnboarding();
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const { isAddToCalendarEnabled } = useFeatureFlags();

  useEffect(() => {
    trackPageView(OnboardingAnalyticsPageView.Notifications);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setNotificationSettings(getNotificationSettings(unmodifiedProfile));
  }, [unmodifiedProfile]);

  const [updateUserNotificationSettings, { isLoading }] =
    useUpdateUserNotificationSettingsMutation();

  const baseTestID = OnboardingNotificationsTestID.Primary;

  const updateNotificationsSettings = useCallback(() => {
    if (isNil(unmodifiedProfile)) {
      return;
    }

    const payload: UpdateNotificationSettingsBody = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveNotificationEmails: notificationSettings.receiveEmails,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveNotificationPush: notificationSettings.receivePush,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveNotificationSms: notificationSettings.receiveSms,
    };

    track(ProfileAnalyticsEvent.SaveChangesEditNotificationSettings, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Notifications,
      ...getChangesForMixpanel({ notifications, payload }),
    });

    void updateUserNotificationSettings({
      body: payload,
      isPatient: unmodifiedProfile.isPatient,
      userExternalId: unmodifiedProfile.externalId,
    }).unwrap();

    setIsAlertOpen(false);

    // Navigate to calendar sync if feature flag is enabled, otherwise go to consent/welcome
    const nextScreen = (() => {
      if (isAddToCalendarEnabled) {
        return OnboardingScreenPath.CalendarSync;
      }

      if (shouldBypassConsentScreen) {
        return OnboardingScreenPath.Welcome;
      }

      return OnboardingScreenPath.Consent;
    })();

    requestAnimationFrame(() => {
      router.replace(nextScreen);
    });
  }, [
    notificationSettings,
    track,
    notifications,
    updateUserNotificationSettings,
    unmodifiedProfile,
    shouldBypassConsentScreen,
    isAddToCalendarEnabled,
  ]);

  const registerNotifications = useCallback(async () => {
    setIsContinueLoading(true);
    try {
      // Web does not support push notifications, skip registration logic
      if (isWeb() || !notificationSettings.receivePush) {
        updateNotificationsSettings();
        return;
      }
      let isPermissionGranted = false;
      if (isIOS()) {
        isPermissionGranted = await registerForPushNotifications();
      } else {
        // on Android this line gets stuck indefinitely sometimes, so we add a timeout and
        // since the user has already expressed interest in push notifications we assume they will grant permission
        // to be fixed properly in https://teamequip.atlassian.net/browse/JN-1895
        isPermissionGranted = await Promise.race([
          registerForPushNotifications(),
          createTimeoutPromise<boolean>({
            // eslint-disable-next-line @typescript-eslint/naming-convention
            resolveValue: true,
            shouldReject: false,
            timeoutMs: toMs({ seconds: 4 }),
          }),
        ]);
      }
      if (isPermissionGranted) {
        updateNotificationsSettings();
      } else {
        setIsAlertOpen(true);
      }
    } catch {
      setIsAlertOpen(true);
    } finally {
      setIsContinueLoading(false);
    }
  }, [
    registerForPushNotifications,
    updateNotificationsSettings,
    notificationSettings.receivePush,
  ]);

  const handleContinue = async () => {
    track(OnboardingAnalyticsEvent.NextButtonPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Notifications,
    });
    await registerNotifications();
  };

  const handleAlertConfirm = () => {
    track(OnboardingAnalyticsEvent.SkipNotificationsConfirmationPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Notifications,
    });
    updateNotificationsSettings();
  };

  const handleAlertDismiss = () => {
    setIsAlertOpen(false);
  };

  return (
    <>
      <ScreenContainer
        backgroundColor={ColorToken.Primary10}
        isScrollView
        isStackLayout
      >
        <VStack
          flex={1}
          gap={24}
          justifyContent="space-between"
          paddingBottom={LayoutToken.OnboardingScreenHorizontalPadding}
          paddingHorizontal={LayoutToken.OnboardingScreenHorizontalPadding}
          paddingTop={LayoutToken.ModalHeaderPadding}
        >
          <VStack gap={24}>
            <VStack gap={12}>
              <Text
                color={ColorToken.Primary90Variant}
                testID={joinTestIDs(baseTestID, TextTestID.Title)}
                variant="h2"
              >
                Notification preferences
              </Text>
              <Text
                color={ColorToken.Primary90Variant}
                testID={joinTestIDs(baseTestID, TextTestID.Description)}
                variant="body"
              >
                At Equip, we use notifications to let you know about new to-dos,
                messages from your provider team, and upcoming appointments.
              </Text>
            </VStack>
            <NotificationSettingsSection
              onChange={setNotificationSettings}
              receiveEmails={notifications.receiveEmails}
              receivePush={notifications.receivePush}
              receiveSms={notifications.receiveSms}
            />
          </VStack>
          <HStack gap={24} justifyContent="flex-end">
            <IconButton
              iconName={FAIconName.ArrowLeft}
              onPress={() =>
                router.replace(OnboardingScreenPath.VerifyInformation)
              }
              shouldShowBorder
              testID={joinTestIDs(baseTestID, "back")}
            />

            <Button
              isFullWidth
              isLoading={isLoading || isContinueLoading}
              leftIcon={FAIconName.ArrowRight}
              onPress={handleContinue}
              testID={joinTestIDs(baseTestID, "continue")}
              title="Next"
            />
          </HStack>
        </VStack>
      </ScreenContainer>
      <ConfirmationAlert
        confirmationType="neutral"
        confirmLeftIcon={FAIconName.ArrowRight}
        confirmText="Continue"
        isOpen={isAlertOpen}
        message={`We recommend turning on push notifications to stay on top of your treatment.
If you'd like to turn on these notifications later, you can navigate to your device Notifications settings, find Equip Health in your list of apps, and toggle 'Allow notifications' on.`}
        onConfirm={handleAlertConfirm}
        onDismiss={handleAlertDismiss}
        testID={ConfirmationAlertTestID.SkipPushNotification}
        title="Push notifications not allowed"
      />
    </>
  );
};

export default Screen;
